/**
 * Event Bridge Utility
 *
 * Provides a simplified interface for cross-window communication between
 * SmarterElements iframes and their parent windows.
 *
 * Includes validation of event types and payloads against defined schemas.
 */

import eventSchemas from './event-schemas';
import { BroadcastResult } from '../types';


/**
 * Get the parent origin for secure communication
 * Falls back to '*' in development but should be configured in production
 * 
 * @returns The parent origin to use for postMessage
 */
function getParentOrigin(): string {
  return '*';
  // In development, we might need to use '*' for local testing
  /*
  if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
    return '*';
  }
  */

  // In production, we should use a specific origin
  // This can be configured via environment variables or other means
  /*
  const configuredOrigin = import.meta.env.VITE_PARENT_ORIGIN;
  if (configuredOrigin) {
    return configuredOrigin;
  }
    */

  // Try to determine parent origin from referrer
  try {
    const referrer = document.referrer;
    if (referrer) {
      const url = new URL(referrer);
      return url.origin;
    }
  } catch (error) {
    console.warn('Could not determine parent origin from referrer:', error);
  }

  // Last resort fallback - should be avoided in production
  console.warn(
    'Using wildcard origin (*) for postMessage. This is not recommended for production.'
  );
  return '*';
}

/**
 * Broadcast an element event to the parent window
 * 
 * @param eventName - The name of the event to broadcast
 * @param elementId - The unique ID of the element instance
 * @param elementType - The type of the element (e.g. 'proctoring/session-viewer')
 * @param data - The data to send with the event
 * @returns Result with success status and any validation errors
 */
export function broadcastElementEvent(
  eventName: string,
  elementType: string,
  elementId: string, 
  data: Record<string, unknown> = {}
): BroadcastResult {
  try {
    // Create the prefixed event name for schema validation
    const prefixedEventName = `${elementType}:${eventName}`;
    
    // Validate the event data against the schema
    const validation = eventSchemas.validateElementEvent(prefixedEventName, data as Record<string, unknown>) as { valid: boolean; errors?: string[] };
    
    if (!validation.valid) {
      console.error('Event validation failed:', validation.errors);
      return {
        success: false,
        errors: (validation.errors || []).map((err: string) => new Error(err))
      }; 
    }

    // Get the parent origin
    const targetOrigin = getParentOrigin();
    
    // Remove elementId and elementType from the payload
    delete data['elementId'];
    delete data['elementType'];

    // Create the message to send
    const message = {
      source: 'smarter-elements',
      event: eventName,      
      elementType,
      elementId,      
      payload: data
    };

    // Send the message to the parent window
    window.parent.postMessage(message, targetOrigin);
    
    return { success: true };
  } catch (error) {
    console.error('Error broadcasting event:', error);
    return {
      success: false,
      errors: [error as Error]
    };
  }
}

/**
 * Listen for events from the parent window
 * 
 * @param eventName - The name of the event to listen for
 * @param callback - The callback to call when the event is received
 * @returns A function to remove the event listener
 */
export function listen(
  eventName: string,
  callback: (data: unknown) => void
): () => void {
  const messageHandler = (event: MessageEvent) => {
    // Verify the message is from a trusted source
    if (event.source !== window.parent) {
      return;
    }

    // Check if this is an elements event
    if (
      typeof event.data === 'object' &&
      event.data !== null &&
      event.data.source === 'smarter-elements' &&
      event.data.event === eventName
    ) {
      callback(event.data.payload);
    }
  };

  // Add the event listener
  window.addEventListener('message', messageHandler);

  // Return a function to remove the listener
  return () => {
    window.removeEventListener('message', messageHandler);
  };
}
